import { useEffect, useState } from 'react'
import { Table, Tag, Avatar, Input, Select, Button, Space, Card, Row, Col, message } from 'antd'
import { UserOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons'
import { userApi } from '../services/api'

interface User {
  id: number
  phone: string
  user_type: number
  status: number
  created_at: string
  individualClient?: {
    nickname?: string
    member_level?: number
    points?: number
  }
  enterpriseClient?: {
    company_name?: string
    contact_name?: string
  }
}

const memberLevelMap: Record<number, { label: string; color: string }> = {
  1: { label: '普通会员', color: 'default' },
  2: { label: '银卡会员', color: 'silver' },
  3: { label: '金卡会员', color: 'gold' },
  4: { label: '钻石会员', color: 'purple' }
}

function Users() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [filters, setFilters] = useState({ keyword: '', user_type: undefined as number | undefined })

  const fetchUsers = async (page = 1, pageSize = 10, extraFilters?: any) => {
    setLoading(true)
    const f = extraFilters || filters
    try {
      const res: any = await userApi.getUsers({
        page,
        page_size: pageSize,
        keyword: f.keyword || undefined,
        user_type: f.user_type || undefined
      })
      if (res.code === 200) {
        setUsers(res.data.list)
        setPagination({ current: page, pageSize, total: res.data.total })
      }
    } catch {
      // 后端暂未实现时显示空列表
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchUsers() }, [])

  const handleSearch = () => fetchUsers(1, pagination.pageSize)
  const handleReset = () => {
    const f = { keyword: '', user_type: undefined }
    setFilters(f)
    fetchUsers(1, pagination.pageSize, f)
  }

  const handleToggleStatus = async (record: User) => {
    const newStatus = record.status === 1 ? 0 : 1
    try {
      await userApi.updateUserStatus(record.id, newStatus)
      message.success(newStatus === 1 ? '已启用' : '已禁用')
      fetchUsers(pagination.current, pagination.pageSize)
    } catch {
      message.error('操作失败')
    }
  }

  const columns = [
    {
      title: '用户',
      key: 'user',
      render: (_: any, record: User) => {
        const name = record.user_type === 2
          ? record.enterpriseClient?.company_name
          : record.individualClient?.nickname || record.phone
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar icon={<UserOutlined />} style={{ backgroundColor: record.user_type === 2 ? '#55266c' : '#1890ff' }} />
            <div>
              <div style={{ fontWeight: 500 }}>{name || '-'}</div>
              <div style={{ fontSize: 12, color: '#999' }}>{record.phone}</div>
            </div>
          </div>
        )
      }
    },
    {
      title: '类型',
      dataIndex: 'user_type',
      key: 'user_type',
      width: 100,
      render: (v: number) => (
        <Tag color={v === 2 ? 'purple' : 'blue'}>{v === 2 ? '企业客户' : '个人客户'}</Tag>
      )
    },
    {
      title: '会员等级',
      key: 'level',
      width: 110,
      render: (_: any, record: User) => {
        if (record.user_type === 2) return <Tag>企业账户</Tag>
        const level = record.individualClient?.member_level || 1
        const { label, color } = memberLevelMap[level] || memberLevelMap[1]
        return <Tag color={color}>{label}</Tag>
      }
    },
    {
      title: '积分',
      key: 'points',
      width: 90,
      render: (_: any, record: User) =>
        record.user_type === 1 ? (record.individualClient?.points ?? 0) : '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (v: number) => (
        <Tag color={v === 1 ? 'success' : 'error'}>{v === 1 ? '正常' : '禁用'}</Tag>
      )
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v: string) => v ? new Date(v).toLocaleDateString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: User) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => handleToggleStatus(record)}
            style={{ color: record.status === 1 ? '#ef4444' : '#10b981' }}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
        </Space>
      )
    }
  ]

  return (
    <div>
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
        <Row gutter={12} align="middle">
          <Col>
            <Input
              placeholder="搜索手机号/名称"
              prefix={<SearchOutlined />}
              value={filters.keyword}
              onChange={e => setFilters(f => ({ ...f, keyword: e.target.value }))}
              onPressEnter={handleSearch}
              style={{ width: 200 }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="用户类型"
              value={filters.user_type}
              onChange={v => setFilters(f => ({ ...f, user_type: v }))}
              style={{ width: 130 }}
              allowClear
            >
              <Select.Option value={1}>个人客户</Select.Option>
              <Select.Option value={2}>企业客户</Select.Option>
            </Select>
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} style={{ backgroundColor: '#55266c' }}>搜索</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 个用户`
        }}
        onChange={(p) => fetchUsers(p.current!, p.pageSize!)}
        size="middle"
      />
    </div>
  )
}

export default Users
